import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './hooks/useAuthStore';

// Pages
import LoginPage from './pages/LoginPage';
// RegisterPage not directly rendered — /register redirects to /welcome
// import RegisterPage from './pages/RegisterPage';
import ChildDashboard from './pages/ChildDashboard';
import ParentDashboard from './pages/ParentDashboard';
import ChatPage from './pages/ChatPage';
import SettingsPage from './pages/SettingsPage';
import AddChildPage from './pages/AddChildPage';
import ModerationPage from './pages/ModerationPage';
import ConversationsPage from './pages/ConversationsPage';
import ProgressPage from './pages/ProgressPage';
import ChildProgressPage from './pages/ChildProgressPage';
import ChildProfilePage from './pages/ChildProfilePage';
import FlaggedContentPage from './pages/FlaggedContentPage';
import NotFoundPage from './pages/NotFoundPage';
import LandingPage from './pages/LandingPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import { WelcomeFlow, ChildSetupFlow, FirstSessionPage } from './pages/onboarding';
import LeaderboardPage from './pages/LeaderboardPage';

// Layouts
import ChildLayout from './components/layouts/ChildLayout';
import ParentLayout from './components/layouts/ParentLayout';

function PrivateRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: string[] }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { isAuthenticated, user } = useAuthStore();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Routes>
        {/* Public Routes */}
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<Navigate to="/welcome" replace />} />
        <Route path="/welcome" element={<WelcomeFlow />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* Onboarding Routes (no layout chrome) */}
        <Route
          path="/parent/setup-child"
          element={
            <PrivateRoute allowedRoles={['parent', 'admin']}>
              <ChildSetupFlow />
            </PrivateRoute>
          }
        />
        <Route
          path="/learn/first-session"
          element={
            <PrivateRoute>
              <FirstSessionPage />
            </PrivateRoute>
          }
        />

        {/* Child Routes */}
        <Route
          path="/learn/*"
          element={
            <PrivateRoute>
              <ChildLayout>
                <Routes>
                  <Route index element={<ChildDashboard />} />
                  <Route path="chat" element={<ChatPage />} />
                  <Route path="chat/:conversationId" element={<ChatPage />} />
                  <Route path="progress" element={<ChildProgressPage />} />
                  <Route path="profile" element={<ChildProfilePage />} />
                  <Route path="leaderboard" element={<LeaderboardPage />} />
                </Routes>
              </ChildLayout>
            </PrivateRoute>
          }
        />

        {/* Parent Routes */}
        <Route
          path="/parent/*"
          element={
            <PrivateRoute allowedRoles={['parent', 'admin']}>
              <ParentLayout>
                <Routes>
                  <Route index element={<ParentDashboard />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="add-child" element={<AddChildPage />} />
                  <Route path="moderation" element={<ModerationPage />} />
                  <Route path="flagged" element={<FlaggedContentPage />} />
                  <Route path="child/:childId" element={<ParentDashboard />} />
                  <Route path="child/:childId/settings" element={<SettingsPage />} />
                  <Route path="child/:childId/conversations" element={<ConversationsPage />} />
                  <Route path="child/:childId/progress" element={<ProgressPage />} />
                  <Route path="children" element={<Navigate to="/parent" replace />} />
                  <Route path="reports" element={<Navigate to="/parent" replace />} />
                  <Route path="*" element={<Navigate to="/parent" replace />} />
                </Routes>
              </ParentLayout>
            </PrivateRoute>
          }
        />

        {/* Root redirect based on auth state */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              user?.role === 'parent' || user?.role === 'admin' ? (
                <Navigate to="/parent" replace />
              ) : (
                <Navigate to="/learn" replace />
              )
            ) : (
              <LandingPage />
            )
          }
        />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
}

export default App;
